import { Modal } from '../../modal';
import { currencyCodeSuffix } from '../../../utils/money';
import styled from 'styled-components';
import tw from 'twin.macro';
import { IGetBookingQuery } from '../../../services/bookingService/__generated__/queries.generated';
import { format } from 'date-fns';
import { BookingActivityType, InvoiceType } from '../../../../__generated__/graphql';
import { tripAllowanceDisplay } from '../../../utils/tripDistance';
import type { DistanceUnit } from '../../../utils/units';

const ReceiptContainer = styled.div`
  ${tw`box-border space-y-6 w-full max-w-full overflow-x-hidden`}
`;

const Header = styled.div`
  ${tw`flex flex-col sm:flex-row justify-between items-start mb-6 gap-4`}
`;

const HeaderLeft = styled.div`
  ${tw`space-y-1 w-full sm:w-auto`}
`;

const HeaderRight = styled.div`
  ${tw`text-left sm:text-right text-sm text-text-secondary w-full sm:w-auto mt-2 sm:mt-0`}
`;

const Title = styled.h2`
  ${tw`text-base font-normal text-text-secondary`}
`;

const CarInfo = styled.div`
  ${tw`space-y-1`}
`;

const CarTitle = styled.h3`
  ${tw`text-xl font-bold break-words`}
`;

const HostName = styled.p`
  ${tw`text-base`}
`;

const TripDates = styled.div`
  ${tw`grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-8 my-6`}
`;

const DateSection = styled.div`
  ${tw`space-y-1`}
`;

const DateLabel = styled.div`
  ${tw`text-sm font-medium`}
`;

const Address = styled.div`
  ${tw`text-sm`}
`;

const GuestSection = styled.div`
  ${tw`flex justify-between items-center py-4 border-t border-b border-border-default`}
`;

const GuestLabel = styled.span`
  ${tw`text-sm`}
`;

const GuestName = styled.span`
  ${tw`text-sm text-text-secondary`}
`;

const ReceiptRow = styled.div`
  ${tw`flex flex-wrap sm:flex-nowrap justify-between items-baseline py-1`}
`;

const Label = styled.span`
  ${tw`text-sm mb-1 sm:mb-0`}
`;

const Value = styled.span`
  ${tw`text-sm`}
`;

const SmallText = styled.span`
  ${tw`text-xs text-text-secondary`}
`;

const TotalRow = styled(ReceiptRow)`
  ${tw`pt-4 border-t border-border-default space-y-4`}
`;

const PaymentSection = styled.div`
  ${tw`mt-4 pt-4 space-y-2`}
`;

const PriceDetails = styled.div`
  ${tw`flex flex-col items-end text-right`}
`;

const PaymentHistorySection = styled.div`
  ${tw`mt-4 pt-4 space-y-2 border-t border-border-default`}
`;

const PaymentDate = styled.span`
  ${tw`text-xs text-text-secondary block`}
`;

const TripTime = styled.div`
  ${tw`text-sm break-words`}
`;

// New styled components for improved receipt
const SummarySection = styled.div`
  ${tw`mt-6 p-4 bg-bg-secondary rounded-lg border border-border-default`}
`;

const SummaryTitle = styled.h4`
  ${tw`text-sm font-semibold text-text-secondary mb-3 uppercase tracking-wide`}
`;

const SummaryRow = styled.div`
  ${tw`flex justify-between items-center py-1`}
`;

const SummaryLabel = styled.span`
  ${tw`text-sm text-text-secondary`}
`;

const SummaryValue = styled.span<{ highlight?: boolean; negative?: boolean }>`
  ${tw`text-sm font-medium`}
  ${props => props.highlight && tw`text-lg font-bold text-text-primary`}
  ${props => props.negative && tw`text-green-600`}
`;

const SummaryDivider = styled.div`
  ${tw`border-t border-border-default my-2`}
`;

const ModificationsSection = styled.div`
  ${tw`mt-6 pt-4 border-t border-border-default`}
`;

const ModificationItem = styled.div`
  ${tw`p-3 bg-bg-secondary rounded-lg mb-2 border-l-4 border-blue-400`}
`;

const ModificationHeader = styled.div`
  ${tw`flex justify-between items-start mb-1`}
`;

const ModificationTitle = styled.span`
  ${tw`text-sm font-medium text-blue-800`}
`;

const ModificationDate = styled.span`
  ${tw`text-xs text-text-secondary`}
`;

const ModificationDetails = styled.div`
  ${tw`text-xs text-text-secondary mt-1`}
`;

const DepositStatusBadge = styled.span<{ status: string }>`
  ${tw`text-xs font-medium px-2 py-1 rounded-full ml-2`}
  ${props => {
    switch (props.status) {
      case 'RELEASED':
        return tw`bg-green-100 text-green-700`;
      case 'AUTHORIZED':
      case 'CHARGED':
        return tw`bg-yellow-100 text-yellow-700`;
      case 'CAPTURED':
        return tw`bg-red-100 text-red-700`;
      default:
        return tw`bg-bg-secondary text-text-secondary`;
    }
  }}
`;

const SectionHeader = styled.h4`
  ${tw`text-sm font-semibold text-text-secondary mb-3 uppercase tracking-wide`}
`;

// Helper function to get activity label
const getActivityLabel = (type: BookingActivityType): string => {
  const normalizedType = type?.toUpperCase?.() ?? type;
  const labels: Record<string, string> = {
    [BookingActivityType.TripExtended]: 'Trip Extended',
    [BookingActivityType.TripShortened]: 'Trip Shortened',
    [BookingActivityType.CarSwapCompleted]: 'Car Swapped',
    [BookingActivityType.CarSwapApproved]: 'Car Swap Approved',
    [BookingActivityType.CancelledByGuest]: 'Cancelled by Guest',
    [BookingActivityType.CancelledByHost]: 'Cancelled by Host',
    [BookingActivityType.DlVerificationRejected]: 'Cancelled - DL Verification Not Passed',
    [BookingActivityType.ProtectionPlanChanged]: 'Protection Plan Changed',
    [BookingActivityType.RefundCompleted]: 'Refund Completed',
    [BookingActivityType.DepositReleased]: 'Deposit Released',
  };
  return labels[normalizedType] || String(type).replace(/_/g, ' ');
};

// Helper to format deposit status
const formatDepositStatus = (status: string): string => {
  const statusMap: Record<string, string> = {
    AUTHORIZED: 'Hold Active',
    CHARGED: 'Charged',
    RELEASED: 'Released',
    PARTIALLY_RELEASED: 'Partially Released',
    PENDING_HOLD: 'Hold Pending',
    HELD: 'Held',
    CAPTURED: 'Captured',
    PENDING: 'Pending',
  };
  return statusMap[status] || status;
};

export type IncidentReceiptLine = {
  label: string;
  amount: number;
  description?: string;
};

const INCIDENT_CHARGE_TYPES = new Set([
  'FUEL_FEE_CHARGE',
  'TOLL_FEE_CHARGE',
  'TICKET_FEE_CHARGE',
  'LATE_FEE',
  'CLEANING_FEE',
  'SMOKING_FEE',
  'DAMAGE_CHARGE',
  'OTHER_FEE_CHARGE',
]);

const incidentLabel = (type?: string) => {
  switch (type?.toUpperCase()) {
    case 'FUEL_FEE_CHARGE':
      return 'Refueling';
    case 'TOLL_FEE_CHARGE':
      return 'Tolls';
    case 'TICKET_FEE_CHARGE':
      return 'Tickets';
    case 'LATE_FEE':
      return 'Late return';
    case 'CLEANING_FEE':
      return 'Cleaning';
    case 'SMOKING_FEE':
      return 'Smoking';
    case 'DAMAGE_CHARGE':
      return 'Damage / claim';
    default:
      return 'Incident charge';
  }
};

export const isSettledReceiptInvoice = (invoice: any) => {
  const status = String(invoice?.status || '').toLowerCase();
  return status !== 'initial' && status !== 'pending' && status !== 'failed';
};

export const isIncidentReceiptInvoice = (invoice: any) => {
  if (invoice?.incidentLineItems?.length) return true;
  return INCIDENT_CHARGE_TYPES.has(String(invoice?.type || '').toUpperCase());
};

export const getIncidentReceiptLines = (invoices?: any[]): IncidentReceiptLine[] =>
  (invoices || [])
    .filter(invoice => isSettledReceiptInvoice(invoice) && isIncidentReceiptInvoice(invoice))
    .flatMap(invoice => {
      if (invoice?.incidentLineItems?.length) {
        return invoice.incidentLineItems.map((line: any) => ({
          label: line.label || incidentLabel(line.incidentType),
          amount: Number(line.amount || 0),
          description: line.description || undefined,
        }));
      }
      return [
        {
          label: incidentLabel(invoice?.type),
          amount: Math.abs(Number(invoice?.amount || 0)),
          description: invoice?.hostDescription || undefined,
        },
      ];
    });

/** Mirrors Flutter [BookingDetail.paidIncidentChargesTotal]. */
export const getPaidIncidentChargesTotal = (receipt: any, invoices?: any[]): number => {
  const backendTotal = receipt?.postTripIncidentChargesTotal;
  if (backendTotal != null && !Number.isNaN(Number(backendTotal))) {
    return Number(backendTotal);
  }
  return (invoices || [])
    .filter(invoice => isSettledReceiptInvoice(invoice) && isIncidentReceiptInvoice(invoice))
    .reduce((sum, invoice) => sum + Math.abs(Number(invoice?.amount || 0)), 0);
};

/** Mirrors Flutter [BookingDetail.receiptGrandTotal]. */
export const getReceiptGrandTotal = (receipt: any, invoices?: any[]): number | null => {
  const backendGrand = receipt?.totalPriceWithPostTripCharges;
  if (backendGrand != null && !Number.isNaN(Number(backendGrand))) {
    return Number(backendGrand);
  }
  const baseRaw = receipt?.totalPriceWithFees ?? receipt?.totalPrice;
  if (baseRaw == null || Number.isNaN(Number(baseRaw))) {
    return null;
  }
  return Number(baseRaw) + getPaidIncidentChargesTotal(receipt, invoices);
};

// Spec 028: in the trip's unit (shared with the trip page).
const getDistanceLimitDisplay = (booking: IGetBookingQuery['booking'], unit: DistanceUnit) =>
  tripAllowanceDisplay(booking as any, unit)!;

interface ReceiptDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  booking: IGetBookingQuery['booking'];
  /** Spec 028: the trip's unit; miles when unknown. */
  distanceUnit?: DistanceUnit;
  /** Spec 028: the trip's currency; CAD adds " CAD" after the totals (D14). */
  currency?: string;
  /** Spec 028: "HST", "GST" or "Tax" (D16). */
  taxLabel?: string;
}

export const ReceiptDetailsModal = ({
  isOpen,
  onClose,
  booking,
  distanceUnit = 'mi',
  currency = 'USD',
  taxLabel = 'Tax',
}: ReceiptDetailsModalProps) => {
  const code = currencyCodeSuffix(currency);
  const { receipt, protectionPlanEntity, invoices, activities, securityDepositStatus, status } =
    booking;
  const incidentReceiptLines = getIncidentReceiptLines(invoices as any[]);
  const incidentChargesTotal = getPaidIncidentChargesTotal(receipt, invoices as any[]);
  const grandTotal = getReceiptGrandTotal(receipt, invoices as any[]) ?? 0;
  const distanceLimitDisplay = getDistanceLimitDisplay(booking, distanceUnit);

  // Check if booking is cancelled (status starts with 'CANCELLED')
  const isCancelled = status?.startsWith('CANCELLED');

  const isDepositInvoice = (type: string) => type?.toUpperCase() === InvoiceType.Deposit; // Only exclude deposit charges, not refunds
  const isDepositRefund = (type?: string) =>
    type?.toUpperCase() === InvoiceType.DepositRefund || type?.toUpperCase() === 'DEPOSIT_REFUND';

  // Filter out $0 payments, exclude deposit charges (but include deposit refunds), and sort by date (newest first)
  const filteredPayments = invoices
    ?.filter(payment => Math.abs(payment.amount) > 0.01)
    ?.filter(payment => isSettledReceiptInvoice(payment))
    ?.filter(payment => !isDepositInvoice(payment.type)) // Exclude deposit charges, but include deposit refunds
    ?.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());

  // Calculate payment summary
  // Handle both GraphQL enum format (FULL_REFUND) and database format (full_refund)
  // Include all refunds: FULL_REFUND, PARTIAL_REFUND, DEPOSIT_REFUND, etc.
  const summaryPayments = filteredPayments?.filter(p => !isDepositRefund(p.type));
  const computedTotalPaid =
    summaryPayments
      ?.filter(p => {
        const type = p.type?.toUpperCase() || '';
        return !type.includes('REFUND');
      })
      ?.reduce((sum, p) => sum + Math.abs(p.amount), 0) ?? 0;

  const computedTotalRefunded =
    summaryPayments
      ?.filter(p => {
        const type = p.type?.toUpperCase() || '';
        return type.includes('REFUND');
      })
      ?.reduce((sum, p) => sum + Math.abs(p.amount), 0) ?? 0;

  const paymentSummary = (booking as any).paymentSummary;
  const totalPaid = paymentSummary?.totalPaid ?? computedTotalPaid;
  const totalRefunded = paymentSummary?.totalRefunded ?? computedTotalRefunded;
  const netAmount = paymentSummary?.netAmount ?? totalPaid - totalRefunded;

  const receiptBalance =
    receipt.balance !== null && receipt.balance !== undefined
      ? Number(receipt.balance)
      : isCancelled && totalRefunded > 0
        ? 0 - totalRefunded
        : null;
  const effectiveRefunded =
    totalRefunded > 0
      ? totalRefunded
      : receiptBalance !== null && receiptBalance < 0
        ? Math.abs(receiptBalance)
        : 0;
  const netAmountWithFallback = totalPaid - effectiveRefunded;
  const paymentHistory = [...(filteredPayments ?? [])];
  const hasRefundInvoice = paymentHistory.some(p =>
    (p.type?.toUpperCase() || '').includes('REFUND'),
  );
  if (isCancelled && receiptBalance !== null && receiptBalance < 0 && !hasRefundInvoice) {
    // Fall back to receipt balance when refund invoices haven't synced yet.
    paymentHistory.push({
      amount: receiptBalance,
      createdAt: booking.cancelledInitiatedAt || booking.updatedAt || booking.createdAt,
      type: 'REFUND_DUE',
    } as any);
  }

  // Filter modification activities (exclude booking_created, payment_received, etc.)
  const modificationTypes = [
    BookingActivityType.TripExtended,
    BookingActivityType.TripShortened,
    BookingActivityType.CarSwapCompleted,
    BookingActivityType.CarSwapApproved,
    BookingActivityType.CancelledByGuest,
    BookingActivityType.CancelledByHost,
    BookingActivityType.DlVerificationRejected,
    BookingActivityType.ProtectionPlanChanged,
  ];

  const modificationActivities = activities
    ?.filter(a => {
      const type = a.activityType?.toUpperCase?.();
      return type ? modificationTypes.includes(type as BookingActivityType) : false;
    })
    ?.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <ReceiptContainer>
        <Header>
          <HeaderLeft>
            <Title>Trip Receipt</Title>
            <CarInfo>
              <HostName>
                {/* {booking.car?.owner?.firstName} {booking.car?.owner?.lastName} */}
                Mark Mu
              </HostName>
              <CarTitle>
                {booking.car?.year} {booking.car?.make} {booking.car?.model}
              </CarTitle>
            </CarInfo>
          </HeaderLeft>
          <HeaderRight>
            <div>Reservation ID {booking.id}</div>
            <div>
              BOOKED {format(booking.createdAt, 'dd MMM, yyyy')} AT{' '}
              {format(booking.createdAt, 'HH:mm')} UTC
            </div>
          </HeaderRight>
        </Header>

        <TripDates>
          <DateSection>
            <DateLabel>TRIP START</DateLabel>
            <TripTime>{format(booking.startAt, 'EEE, dd MMM, yyyy: HH:mm zzz')}</TripTime>
            <Address>
              {booking.car?.location?.address}
              <br />
              {booking.car?.location?.city}, {booking.car?.location?.state}
              <br />
              {booking.car?.location?.zipCode}
            </Address>
          </DateSection>
          <DateSection>
            <DateLabel>TRIP END</DateLabel>
            <TripTime>{format(booking.endAt, 'EEE, dd MMM, yyyy: HH:mm zzz')}</TripTime>
            <Address>
              {booking.car?.location?.address}
              <br />
              {booking.car?.location?.city}, {booking.car?.location?.state}
              <br />
              {booking.car?.location?.zipCode}
            </Address>
          </DateSection>
        </TripDates>

        <GuestSection>
          <GuestLabel>GUEST</GuestLabel>
          <GuestName>
            {booking.guest?.firstName} {booking.guest?.lastName}
          </GuestName>
        </GuestSection>

        <div className="space-y-2 mt-4">
          <ReceiptRow>
            <Label>Distance limit</Label>
            <PriceDetails>
              <Value>{distanceLimitDisplay.value}</Value>
              <SmallText className="block">{distanceLimitDisplay.note}</SmallText>
            </PriceDetails>
          </ReceiptRow>

          <ReceiptRow>
            <Label>Trip Price</Label>
            <PriceDetails>
              <Value>${(receipt.pricePerDay ?? 0) * (receipt?.totalDays ?? 0)}</Value>
              <SmallText className="block">
                ({receipt?.totalDays} days @ ${receipt.pricePerDay}/day)
              </SmallText>
              {receipt?.lastMinSurchargeAmount && receipt.lastMinSurchargeAmount > 0 ? (
                <SmallText className="block" style={{ color: '#D97706' }}>
                  Incl. {receipt.lastMinSurchargePercentage}% last-min surcharge (+$
                  {receipt.lastMinSurchargeAmount.toFixed(2)})
                </SmallText>
              ) : null}
            </PriceDetails>
          </ReceiptRow>

          {receipt.discountAmount ? (
            <ReceiptRow>
              <Label>Trip Discount</Label>
              <PriceDetails>
                <Value>${-receipt.discountAmount}</Value>
                <SmallText className="block">
                  ({receipt.discountTier} - {receipt.discountPercentage}% off, discount)
                </SmallText>
              </PriceDetails>
            </ReceiptRow>
          ) : null}

          {receipt.tripFeeTotal ? (
            <ReceiptRow>
              <Label>TRIP FEE</Label>
              <PriceDetails>
                <Value>${receipt.tripFeeTotal.toFixed(2)}</Value>
                {receipt.totalDays ? (
                  <SmallText className="block">
                    ({receipt.totalDays} days@ {receipt.tripFeeTotal / receipt.totalDays}/day)
                  </SmallText>
                ) : null}
              </PriceDetails>
            </ReceiptRow>
          ) : null}

          {receipt.deliveryFee && receipt.deliveryFee > 0 ? (
            <ReceiptRow>
              <Label>Delivery Fee</Label>
              <PriceDetails>
                <Value>${receipt.deliveryFee.toFixed(2)}</Value>
              </PriceDetails>
            </ReceiptRow>
          ) : null}

          <ReceiptRow>
            <Label>{taxLabel}</Label>
            <PriceDetails>
              <Value>${receipt.tax ?? 0}</Value>
              <SmallText className="block">tax rate @ {(receipt.taxRate ?? 0) * 100}%</SmallText>
            </PriceDetails>
          </ReceiptRow>

          {receipt.couponDiscount && receipt.couponDiscount > 0 ? (
            <ReceiptRow>
              <Label>Coupon Discount {receipt.couponCode ? `(${receipt.couponCode})` : ''}</Label>
              <PriceDetails>
                <Value style={{ color: '#10b981' }}>-${receipt.couponDiscount.toFixed(2)}</Value>
                <SmallText className="block">Applied discount</SmallText>
              </PriceDetails>
            </ReceiptRow>
          ) : null}

          {protectionPlanEntity?.plan ? (
            <ReceiptRow>
              <Label>{protectionPlanEntity?.plan} PROTECTION</Label>
              <PriceDetails>
                <Value>${receipt.insuranceByTrip?.toFixed(2)}</Value>
                <SmallText className="block">
                  {receipt?.totalDays} days @ ${protectionPlanEntity?.dailyPrice}/day (tax included)
                </SmallText>
              </PriceDetails>
            </ReceiptRow>
          ) : null}

          {receipt.securityDeposit && receipt.securityDeposit > 0 ? (
            <ReceiptRow>
              <Label>
                Security Deposit
                {securityDepositStatus && (
                  <DepositStatusBadge status={securityDepositStatus}>
                    {formatDepositStatus(securityDepositStatus)}
                  </DepositStatusBadge>
                )}
              </Label>
              <PriceDetails>
                <Value>${receipt.securityDeposit.toFixed(2)}</Value>
                <SmallText className="block">
                  {securityDepositStatus === 'RELEASED'
                    ? 'Deposit has been released'
                    : 'Held until trip completion + 80 hours'}
                </SmallText>
              </PriceDetails>
            </ReceiptRow>
          ) : null}

          {/* Only show REFUND DUE for cancelled bookings - for active bookings, the line items are already updated */}
          {isCancelled && receiptBalance !== null && receiptBalance !== 0 ? (
            <PaymentSection>
              <ReceiptRow>
                <Label>{receiptBalance < 0 ? 'REFUND DUE' : 'BALANCE DUE'}</Label>
                <Value style={{ color: receiptBalance < 0 ? '#16a34a' : 'inherit' }}>
                  {receiptBalance < 0 ? '-' : ''}${Math.abs(receiptBalance).toFixed(2)}
                </Value>
              </ReceiptRow>
            </PaymentSection>
          ) : null}

          {receipt.cancellationPenalty ? (
            <PaymentSection>
              <ReceiptRow>
                <Label>CANCELLATION PENALTY</Label>
                <Value>${receipt.cancellationPenalty.toFixed(2)}</Value>
              </ReceiptRow>
            </PaymentSection>
          ) : null}
          {receipt.shortenTripPenalty ? (
            <PaymentSection>
              <ReceiptRow>
                <Label>SHORTEN TRIP PENALTY</Label>
                <Value>${receipt.shortenTripPenalty.toFixed(2)}</Value>
              </ReceiptRow>
            </PaymentSection>
          ) : null}
          {receipt.freeCancellationServiceFee ? (
            <PaymentSection>
              <ReceiptRow>
                <Label>SERVICE FEE</Label>
                <Value>${receipt.freeCancellationServiceFee.toFixed(2)}</Value>
              </ReceiptRow>
            </PaymentSection>
          ) : null}

          {incidentReceiptLines.length > 0 ? (
            <PaymentSection>
              <SectionHeader>Post-trip charges</SectionHeader>
              {incidentReceiptLines.map((line, index) => (
                <ReceiptRow key={`${line.label}-${index}`}>
                  <Label>{line.label}</Label>
                  <PriceDetails>
                    <Value>${line.amount.toFixed(2)}</Value>
                    {line.description ? <SmallText>{line.description}</SmallText> : null}
                  </PriceDetails>
                </ReceiptRow>
              ))}
              <ReceiptRow>
                <Label>Post-trip charges total</Label>
                <Value>${incidentChargesTotal.toFixed(2)}</Value>
              </ReceiptRow>
            </PaymentSection>
          ) : null}

          <TotalRow>
            <Label>TRIP TOTAL</Label>
            <Value>
              ${grandTotal.toFixed(2)}
              {code}
            </Value>
          </TotalRow>
        </div>

        {/* Trip Modifications Section */}
        {modificationActivities && modificationActivities.length > 0 && (
          <ModificationsSection>
            <SectionHeader>Trip Modifications</SectionHeader>
            {modificationActivities.map((activity, index) => {
              // Safely parse dates
              const createdAtDate = activity.createdAt ? new Date(activity.createdAt) : null;
              const prevEndAt = activity.previousState?.endAt;
              const newEndAt = activity.newState?.endAt;
              const prevEndDate =
                prevEndAt && !isNaN(Number(prevEndAt)) ? new Date(Number(prevEndAt)) : null;
              const newEndDate =
                newEndAt && !isNaN(Number(newEndAt)) ? new Date(Number(newEndAt)) : null;

              return (
                <ModificationItem key={index}>
                  <ModificationHeader>
                    <ModificationTitle>{getActivityLabel(activity.activityType)}</ModificationTitle>
                    <ModificationDate>
                      {createdAtDate && !isNaN(createdAtDate.getTime())
                        ? format(createdAtDate, 'MMM dd, yyyy h:mm a')
                        : 'N/A'}
                    </ModificationDate>
                  </ModificationHeader>
                  <ModificationDetails>
                    {prevEndDate &&
                      newEndDate &&
                      !isNaN(prevEndDate.getTime()) &&
                      !isNaN(newEndDate.getTime()) && (
                        <div>
                          {format(prevEndDate, 'MMM dd')} → {format(newEndDate, 'MMM dd')}
                        </div>
                      )}
                    {activity.amount && activity.amount !== 0 && (
                      <div>
                        {activity.amount > 0 ? 'Charged: ' : 'Refund: '}
                        <span style={{ color: activity.amount > 0 ? '#dc2626' : '#16a34a' }}>
                          {activity.amount > 0 ? '+' : '-'}${Math.abs(activity.amount).toFixed(2)}
                        </span>
                      </div>
                    )}
                    {activity.note && <div style={{ fontStyle: 'italic' }}>{activity.note}</div>}
                  </ModificationDetails>
                </ModificationItem>
              );
            })}
          </ModificationsSection>
        )}

        {/* Payment Summary Section */}
        <SummarySection>
          <SummaryTitle>Payment Summary</SummaryTitle>
          <SummaryRow>
            <SummaryLabel>Total Paid</SummaryLabel>
            <SummaryValue>
              ${totalPaid.toFixed(2)}
              {code}
            </SummaryValue>
          </SummaryRow>
          {effectiveRefunded > 0 && (
            <SummaryRow>
              <SummaryLabel>Total Refunded</SummaryLabel>
              <SummaryValue negative>-${effectiveRefunded.toFixed(2)}</SummaryValue>
            </SummaryRow>
          )}
          <SummaryDivider />
          <SummaryRow>
            <SummaryLabel>Net Amount</SummaryLabel>
            <SummaryValue highlight>
              ${netAmountWithFallback.toFixed(2)}
              {code}
            </SummaryValue>
          </SummaryRow>
        </SummarySection>

        {/* Payment History Section (filtered) */}
        <PaymentHistorySection>
          <SectionHeader>Payment History</SectionHeader>
          {paymentHistory.map((payment, index) => {
            const type = payment.type?.toUpperCase() || '';
            const isRefund = type.includes('REFUND');
            const isDepositRefund = type === 'DEPOSIT_REFUND' || type === 'DEPOSITREFUND';
            const isIncidentCharge = isIncidentReceiptInvoice(payment);
            const label = isDepositRefund
              ? 'SECURITY DEPOSIT RELEASED'
              : isRefund
                ? 'REFUND'
                : isIncidentCharge
                  ? 'POST-TRIP CHARGE'
                  : 'YOU PAID';
            return (
              <ReceiptRow key={index}>
                <Label>{label}</Label>
                <div>
                  <Value style={{ color: isRefund ? '#16a34a' : 'inherit' }}>
                    {isRefund ? '- ' : ''}${Math.abs(payment.amount).toFixed(2)}
                  </Value>
                  <PaymentDate>
                    **** **** **** {receipt.last4digitOfCard} on{' '}
                    {format(payment.createdAt, 'EEE, MMM dd hh:mm a')}
                  </PaymentDate>
                </div>
              </ReceiptRow>
            );
          })}
          {(!filteredPayments || filteredPayments.length === 0) && (
            <SmallText>No payment records</SmallText>
          )}
        </PaymentHistorySection>
      </ReceiptContainer>
    </Modal>
  );
};
